import {
  AppAuthRefeshTokenKey,
  LocaleKey,
  AppAuthTokenKey
} from "@/libs/constant";
import type { AxiosInstance } from "axios";
import axios from "axios";
import { boot } from "quasar/wrappers";
import { Cookies } from "quasar";
import { useAuthenStore } from "@/stores/authenStore";
import { ACCOUNT_ROUTE_NAME } from "@/libs/route-name";

declare module "@vue/runtime-core" {
  interface ComponentCustomProperties {
    $axios: AxiosInstance;
    $api: AxiosInstance;
  }
}

const api = axios.create({
  // baseURL: process.env.NODE_ENV == 'development' ? 'http://192.168.7.249:8080' : 'https://api.example.com',
  baseURL: import.meta.env.VITE_API_BASE_URL || "",
  withCredentials: false,
  timeout: import.meta.env.VITE_API_TIMEOUT
    ? +import.meta.env.VITE_API_TIMEOUT
    : 3 * 60000, // 60000 = 1 minute, 0 = no timeout
  headers: {
    "Content-Type": "application/json",
    "Accept-Apiclient": import.meta.env.VITE_API_CLIENT
  },
  validateStatus: status => status < 400 // Resolve only if the status code is less than 400
  // validateStatus: (status) => status <= 500 // Resolve only if the status code is less than 500
});

// for multiple requests
let isRefreshing = false;
let failedQueue: any[] = [];
const processQueue = (error: any, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

export default boot(({ app, redirect, store, router, ssrContext }) => {
  const authenStore = useAuthenStore(store);
  const isServer = process.env.SERVER;

  if (!isServer) {
    api.interceptors.request.use(
      async config => {
        try {
          const token = Cookies.get(AppAuthTokenKey);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          const locale = Cookies.get(LocaleKey);
          if (locale) {
            config.headers["Accept-Language"] = locale;
          }
        } catch (error) {
          console.warn("Failed to get cookies in axios interceptor:", error);
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    api.interceptors.response.use(
      response => {
        return response;
      },
      async error => {
        const originalRequest = error.config;
        let refreshToken = null;

        try {
          refreshToken = Cookies.get(AppAuthRefeshTokenKey);
        } catch (err) {
          console.warn("Failed to get refresh token:", err);
        }

        if (originalRequest.url?.includes("/api/v1/auth/refresh-token")) {
          router.push({ name: ACCOUNT_ROUTE_NAME.ACCOUNT });
          return Promise.reject(error);
        }

        if (
          refreshToken &&
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry
        ) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(token => {
                originalRequest.headers.Authorization = "Bearer " + token;
                return api(originalRequest);
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          // eslint-disable-next-line no-async-promise-executor
          return new Promise(async (resolve, reject) => {
            try {
              const refreshApi = axios.create({
                baseURL: import.meta.env.VITE_API_BASE_URL || "",
                timeout: 30000,
                headers: {
                  "Content-Type": "application/json",
                  "Accept-Apiclient": import.meta.env.VITE_API_CLIENT
                }
              });

              const response = await refreshApi.post(
                "/api/v1/auth/refresh-token",
                {
                  refreshToken
                }
              );

              let tokenData;
              if (
                response.data &&
                response.data.data &&
                response.data.data.token
              ) {
                // Format: { data: { token: { accessToken, refreshToken } } }
                tokenData = response.data.data.token;
              } else if (
                response.data &&
                response.data.accessToken &&
                response.data.refreshToken
              ) {
                // Format: { accessToken, refreshToken }
                tokenData = response.data;
              } else {
                throw new Error("Invalid refresh token response format");
              }

              if (tokenData.accessToken && tokenData.refreshToken) {
                await authenStore.setRefreshTokenCookie(undefined, tokenData);
                originalRequest.headers.Authorization =
                  "Bearer " + tokenData.accessToken;
                processQueue(null, tokenData.accessToken);
                resolve(api(originalRequest));
              } else {
                throw new Error(
                  "Missing accessToken or refreshToken in response"
                );
              }
            } catch (errRefresh) {
              processQueue(errRefresh, null);

              authenStore.logout();
              try {
                Cookies.remove(AppAuthTokenKey);
                Cookies.remove(AppAuthRefeshTokenKey);
              } catch (err) {
                console.warn("Failed to remove cookies:", err);
              }
              router.push({ name: ACCOUNT_ROUTE_NAME.ACCOUNT });

              reject(errRefresh);
            } finally {
              isRefreshing = false;
            }
          });
        }

        // If no refresh token or different error, redirect to auth on 401
        if (error.response && error.response.status === 401) {
          authenStore.logout();
          try {
            Cookies.remove(AppAuthTokenKey);
            Cookies.remove(AppAuthRefeshTokenKey);
          } catch (err) {
            console.warn("Failed to remove cookies:", err);
          }
          router.push({ name: ACCOUNT_ROUTE_NAME.ACCOUNT });
        }

        return Promise.reject(error);
      }
    );
  }
  app.config.globalProperties.$axios = axios;
  app.config.globalProperties.$api = api;
});

export { api };
